import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { TDispatch, TRootState } from "../../../redux/types";
import {
  deleteObject,
  fetchObjectsList,
  incrementObjectsPageNo,
  getObjectById,
  setObjectsPageNo,
} from "./ducks";

import Drawer from "@components/Drawer";
import { OldInput } from "@components/OldInput";
import { Text } from "@components/Text";
import ExpandableRow from "@components/ExpandableRow";
import { Button } from "@components/Button";
import { Flex } from "reflexbox";
import { AddButton } from "@components/AddButton";
import { ScrollableList } from "@components/ScrollableList";
import { Column } from "@components/ExpandableRow/styles";
import { useSwal } from "@hooks/useSwal";
import { useHistory, useLocation } from "react-router-dom";
import { useFormatMessage } from "@hooks/formatMessage";

import ObjectsModal from "./Modal/ObjectsModal";

import { getParameterByName } from "../../../utils/toSearchParams";
import { deserialize } from "../../../api/jsonApiParser";
import { ReactComponent as IconEdit } from "../../../assets/icons/edit.svg";
import { ReactComponent as IconTrash } from "../../../assets/icons/trash.svg";
import { ReactComponent as IconFilter } from "../../../assets/icons/filter.svg";

import "../../../styles/index.scss";
import "./Objects.scss";

enum formMode {
  edit = "edit",
  password = "password",
}

const Objects: FC = () => {
  const [filtersOpened, setFiltersOpened] = useState(window.innerWidth >= 1400);
  const [modalContent, setModalContent] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(undefined);
  const [addObjectForm, setAddObjectForm] = useState(undefined);
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  const [showMoreSpinner, setShowMoreSpinner] = useState(false);
  const [objectFilter, setObjectFilter] = useState({
    GenericText: null,
  });

  const history = useHistory();
  const location = useLocation();
  const formatMessage = useFormatMessage();
  const dispatch: TDispatch = useDispatch();

  const { objectsList, moreObjects, objectsPageNo } = useSelector(
    ({ ObjectsReducer: { objects, moreObjects, objectsPageNo } }: TRootState) => ({
      objectsList: objects,
      moreObjects,
      objectsPageNo,
    }),
    shallowEqual
  );

  if (location.search && location.search.length > 0) {
    setModalContent(getParameterByName("content", location.search));
    setModalIsOpen(!!modalContent);
  }

  useEffect(() => {
    dispatch(fetchObjectsList()).then(() => dispatch(incrementObjectsPageNo()));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (!showMoreSpinner && moreObjects) {
      setShowMoreSpinner(true);
      dispatch(fetchObjectsList(objectsPageNo, 10, objectFilter)).then(() => {
        setShowMoreSpinner(false);
        dispatch(incrementObjectsPageNo());
      });
    }
  };

  const changeFilterOpened = () => {
    setFiltersOpened(prevState => !prevState);
  };

  const filterObjects = () => {
    dispatch(setObjectsPageNo(1));
    dispatch(fetchObjectsList(1, 10, objectFilter));
  };

  const clearFilters = () => {
    setObjectFilter({
      GenericText: null,
    });

    dispatch(setObjectsPageNo(1));
    dispatch(fetchObjectsList(1, 10));
  };

  const openModal = (content, message?, entry?) => {
    if (content === formMode.edit) {
      getObjectById(entry.id).then(ret => {
        if (ret) {
          setAddObjectForm(deserialize(ret.data));
          setModalIsOpen(true);
          setModalContent(content);
        }
      });
    } else {
      setAddObjectForm({});
      setModalIsOpen(true);
      setModalContent(content);
    }
  };

  const saveHistoryAndFilterObjects = () => {
    history.push({
      search: "",
    });
    filterObjects();
  };

  const closeModal = () => {
    setModalIsOpen(false);
    saveHistoryAndFilterObjects();

    // TODO: this.props.fetchCompanies().then(res => this.setState({ parentCompanyList: res }));
    // TODO: change the location of the list to the modal component
  };

  const showSuccessMessage = (action, callbackFunction) => {
    swalAlertWithCallback(
      {
        text: `Object has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      callbackFunction
    );
  };

  const remove = id => {
    swalConfirmationMessage({
      text: "Do you really want to remove this object?",
      type: "info",
      cancelationText: "Cancel",
      confirmationText: "Remove object",
    }).then(value => {
      if (value) {
        deleteObject(id).then(ret => {
          if (ret) showSuccessMessage("removed", saveHistoryAndFilterObjects);
        });
      }
    });
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Large Modal"
        className={"large-modal"}
        overlayClassName={"large-modal-overlay"}
      >
        {modalIsOpen && modalContent !== formMode.password && (
          <ObjectsModal closeModal={closeModal} initialData={addObjectForm} />
        )}
      </Modal>

      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <Flex flexDirection="column" width="100%">
          <AddButton
            id="addObjectBtn"
            p="0 32px"
            m="32px 0"
            label={formatMessage("app.addnewobject", "Add Object")}
            onClick={() => {
              setAddObjectForm({});
              setModalIsOpen(true);
            }}
          />
          <ScrollableList
            items={objectsList}
            onLoadMore={handleLoadMore}
            renderHeader={
              <>
                <Column>
                  <Text>{formatMessage("app.objectName", "Object")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.applicationName", "Application")}</Text>
                </Column>
                <Column>
                  <Text>{formatMessage("app.objectTypeName", "Object Type")}</Text>
                </Column>
                <Column icon>
                  <Text>
                    <div
                      id="toggleFilterArea"
                      className="icon-filter"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        changeFilterOpened();
                      }}
                    >
                      <IconFilter />
                    </div>
                  </Text>
                </Column>
              </>
            }
            renderItem={(item, index) => (
              <ExpandableRow
                data-cy="objectRow"
                key={"objectRow" + index}
                id={item.id as string}
                translucent={false}
                expandable={false}
                top={
                  <React.Fragment>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.objectName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.applicationName}
                        </Text>
                      </div>
                    </Column>
                    <Column>
                      <div className="d-flex flex-row showChildOnHover">
                        <Text light={false} fontSize="16px">
                          {item.objectTypeName}
                        </Text>
                      </div>
                    </Column>
                    <Column icon>
                      <div className="d-flex flex-row showChildOnHover">
                        <div
                          data-cy="changeEntry"
                          className="iconWrapper pointer large"
                          title="Edit Object"
                          style={{}}
                          onClick={() => openModal(formMode.edit, "", item)}
                        >
                          <IconEdit />
                        </div>
                        <div
                          data-cy="deleteEntry"
                          className="iconWrapper pointer large"
                          title="Delete Object"
                          style={{}}
                          onClick={() => remove(item.id)}
                        >
                          <IconTrash />
                        </div>
                      </div>
                    </Column>
                  </React.Fragment>
                }
              />
            )}
          />
        </Flex>
        <Drawer id="filterArea" opened={filtersOpened}>
          <div className="paddingContainer">
            <p className="colorGrey mb-1">Dynamic Filter</p>
            <div data-cy="dynamicFilterByField">
              <OldInput
                id="dynamicSearch"
                name="dynamicSearch"
                key="dynamicSearch"
                inputWrapperClass="text-input filter-input"
                placeholder={formatMessage("app.anyfield", "Any Field")}
                value={objectFilter.GenericText}
                onChange={(name, value) =>
                  setObjectFilter(prevState => ({ ...prevState, GenericText: value }))
                }
                validationMessageConfig={null /* this.validationMessageConfig() */}
                type="text"
                inputWrapperStyle={{}}
                filtering
              />
            </div>

            <div className="breaker mb-5" />

            <div className="modal-buttons">
              <Button id="buttonClearSearch" textButton mr="10px" onClick={clearFilters}>
                {formatMessage("app.clear", "Clear")}
              </Button>

              <Button id="buttonSearch" textButton mr="10px" onClick={filterObjects}>
                {formatMessage("app.search", "Search")}
              </Button>
            </div>
          </div>
        </Drawer>
      </Flex>
    </>
  );
};

export default Objects;
